import { FormContent, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Choice, Errors } from "../requests/controls";
import Content from "./content";
import type { Review } from "./types";
import {
  locationJobPath,
  locationJobBillingReviewPath,
  newLocationJobBillingReviewPath,
  locationJobBillingReviewPublicationPath,
  locationJobBillingReviewApprovalPath,
  locationJobBillingReviewConfirmationPath,
  newLocationRecordingOperationalNotificationPath,
  locationRecordingOperationalNotificationsPath,
} from "@/routes";

export default function Show({
  job_id,
  review,
  financial,
  viewing_url,
  approval_time,
  sent_revision,
  history,
  history_pagination,
}: {
  job_id: string;
  review: Review;
  financial: boolean;
  viewing_url: string | null;
  approval_time: string;
  sent_revision: string | null;
  history_pagination: { page: number; pages: number };
  history: {
    id: string;
    actor: string | null;
    access: string | null;
    state: string;
    review: Review;
    at: string;
  }[];
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location.id;
  const confirmation = (
    <Label className="flex items-start gap-2">
      <Checkbox required name="confirmed" value="1" />
      {t("billing_reviews.confirm_terms")}
    </Label>
  );
  const command = (action: string, title: string) => (
    <Form action={action} method="post" className="space-y-4">
      {({ processing }) => (
        <>
          <input type="hidden" name="revision" value={review.revision} />
          <input
            type="hidden"
            name="sent_revision"
            value={sent_revision || ""}
          />
          {confirmation}
          <Button type="submit" disabled={processing}>
            {title}
          </Button>
        </>
      )}
    </Form>
  );
  return (
    <JobWorkspace
      primaryAction={false}
      breadcrumbs={[
        { title: t("jobs.booking"), href: locationJobPath(location, job_id) },
      ]}
    >
      <Head title={t("billing_reviews.title")} />
      <PageCanvas padded={false} data-review-state={review.state}>
        <Errors />
        <Content review={review} headingTag="h2" />
        <p className="font-medium">
          {t(`billing_reviews.states.${review.state}`)}
        </p>
        {!review.current && review.state !== "confirmed" && (
          <p role="alert">{t("billing_reviews.stale")}</p>
        )}
        <section
          data-layout-frame="billing-details"
          className="space-y-2 rounded-lg border p-4"
        >
          <h2 className="font-medium">
            {t("billing_reviews.billing_details")}
          </h2>
          {Object.entries(review.billing_details).map(([key, value]) => (
            <p key={key} className="break-words">
              {t(`billing_reviews.${key}`)}: {value}
            </p>
          ))}
          <p className="text-muted-foreground text-sm">
            {t("billing_reviews.details_help")}
          </p>
        </section>
        {review.state === "draft" &&
          review.current &&
          command(
            locationJobBillingReviewPublicationPath(
              location,
              job_id,
              review.id,
            ),
            t("billing_reviews.publish"),
          )}
        {review.state === "proposed" && review.current && (
          <section
            data-layout-frame="billing-offline-approval"
            className="rounded-lg border p-4"
          >
            <FormContent className="space-y-4">
              <h2 className="font-medium">{t("billing_reviews.offline")}</h2>
              <Form
                action={locationJobBillingReviewApprovalPath(
                  location,
                  job_id,
                  review.id,
                )}
                method="post"
                className="space-y-4"
              >
                {({ processing }) => (
                  <>
                    <input
                      type="hidden"
                      name="revision"
                      value={review.revision}
                    />
                    <Choice
                      id="approval-method"
                      name="approval[method]"
                      label={t("billing_reviews.method")}
                      options={[
                        { id: "phone", name: t("billing_reviews.phone") },
                        {
                          id: "in_person",
                          name: t("billing_reviews.in_person"),
                        },
                      ]}
                    />
                    <Label htmlFor="approval-who">
                      {t("billing_reviews.who")}
                    </Label>
                    <Input id="approval-who" required name="approval[who]" />
                    <Label htmlFor="approval-at">
                      {t("billing_reviews.at")}
                    </Label>
                    <Input
                      id="approval-at"
                      required
                      name="approval[at]"
                      defaultValue={approval_time}
                    />
                    <Label htmlFor="approval-what">
                      {t("billing_reviews.what")}
                    </Label>
                    <Textarea
                      id="approval-what"
                      required
                      name="approval[what]"
                    />
                    {confirmation}
                    <Button disabled={processing} type="submit">
                      {t("billing_reviews.record_approval")}
                    </Button>
                  </>
                )}
              </Form>
            </FormContent>
          </section>
        )}
        {Object.keys(review.approval).length > 0 && (
          <section
            data-layout-frame="billing-saved-approval"
            className="space-y-2 rounded-lg border p-4"
          >
            <h2 className="font-medium">
              {t("billing_reviews.approval_saved")}
            </h2>
            {["method", "who", "at", "what"].map(
              (key) =>
                review.approval[key] && (
                  <p key={key} className="break-words">
                    {t(`billing_reviews.${key}`)}:{" "}
                    {key === "method"
                      ? t(
                          `billing_reviews.${review.approval[key] === "link" ? "link_access" : review.approval[key]}`,
                        )
                      : review.approval[key]}
                  </p>
                ),
            )}
          </section>
        )}
        {financial &&
          review.current &&
          ["proposed", "approved"].includes(review.state) &&
          (!review.increase || review.state === "approved") &&
          command(
            locationJobBillingReviewConfirmationPath(
              location,
              job_id,
              review.id,
            ),
            t("billing_reviews.confirm"),
          )}
        {review.state === "confirmed" && (
          <p role="status">{t("billing_reviews.confirmed_notice")}</p>
        )}
        {viewing_url && (
          <section className="space-y-3">
            <Label htmlFor="review-link">{t("billing_reviews.link")}</Label>
            <Input
              id="review-link"
              readOnly
              value={viewing_url}
              onFocus={(event) => event.target.select()}
            />
            <a className="underline" href={viewing_url}>
              {t("billing_reviews.open")}
            </a>
          </section>
        )}
        {review.state === "proposed" && review.current && (
          <Button
            variant="outline"
            render={
              <Link
                href={newLocationRecordingOperationalNotificationPath(
                  location,
                  review.id,
                )}
              />
            }
          >
            {t("notifications.preview")}
          </Button>
        )}
        {review.state !== "draft" && (
          <Link
            className="block underline"
            href={locationRecordingOperationalNotificationsPath(
              location,
              review.id,
            )}
          >
            {t("notifications.history")}
          </Link>
        )}
        <section className="space-y-2">
          <h2 className="font-medium">{t("proposals.history")}</h2>
          {history.map((event) => (
            <details
              key={event.id}
              data-layout-frame=""
              className="rounded-lg border p-4"
            >
              <summary className="cursor-pointer break-words">
                {event.actor ||
                  t(
                    `billing_reviews.${event.access === "link" ? "link_access" : event.access || "staff"}`,
                  )}{" "}
                · {t(`billing_reviews.states.${event.state}`)} · {event.at}
              </summary>
              <div className="mt-4 space-y-3">
                <Content review={event.review} headingTag="h3" />
                {Object.entries(event.review.billing_details).map(
                  ([key, value]) => (
                    <p key={key} className="break-words">
                      {t(`billing_reviews.${key}`)}: {value}
                    </p>
                  ),
                )}
                {["who", "at", "what"].map(
                  (key) =>
                    event.review.approval[key] && (
                      <p key={key} className="break-words">
                        {t(`billing_reviews.${key}`)}:{" "}
                        {event.review.approval[key]}
                      </p>
                    ),
                )}
              </div>
            </details>
          ))}
          {history_pagination.pages > 1 && (
            <nav
              className="flex flex-wrap gap-4"
              aria-label={t("proposals.history")}
            >
              {history_pagination.page > 1 && (
                <Link
                  className="underline"
                  href={locationJobBillingReviewPath(
                    location,
                    job_id,
                    review.id,
                    { page: history_pagination.page - 1 },
                  )}
                >
                  {t("customers.previous")}
                </Link>
              )}
              {history_pagination.page < history_pagination.pages && (
                <Link
                  className="underline"
                  href={locationJobBillingReviewPath(
                    location,
                    job_id,
                    review.id,
                    { page: history_pagination.page + 1 },
                  )}
                >
                  {t("customers.next")}
                </Link>
              )}
            </nav>
          )}
        </section>
        <Link
          className="block underline"
          href={newLocationJobBillingReviewPath(location, job_id)}
        >
          {t("billing_reviews.prepare")}
        </Link>
        <Link
          className="block underline"
          href={locationJobPath(location, job_id)}
        >
          {t("jobs.booking")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
